import {
  colors,
  colors2,
  ellipsis,
  getBreakPoint,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_CAPTION_SEMIBOLD_DF,
} from "oolib";
import styled, { css } from "styled-components";
import { TABLE_BORDER_RADIUS } from "./CONSTANTS";

const applyFreezeFirstColCss =
  ({ row_header }) =>
  ({ freezeFirstCol, cellIdx, isLastRow }) =>
    freezeFirstCol &&
    cellIdx === 0 &&
    css`
      position: sticky;
      left: 0;
      z-index: ${row_header === "header" ? 10 : 1};
      border-right-width: 4px;
      /* A body cell repeats its row's paint (--row-bg: TableRowElem's stripe,
         hover, disableRowStripes) over white — the greys are translucent, and
         this cell must hide the columns scrolling under it. Its own stripe
         used to be the inverse parity and a different grey. The header cell
         stays white. */
      background: ${row_header === "header"
        ? colors.white
        : `linear-gradient(var(--row-bg, transparent), var(--row-bg, transparent)), ${colors.white}`};
      /* This cell paints its own background (it has to — it slides over the
         other columns), and a square corner inside the frame's rounded one
         fills the curve in. So the cell at a corner is rounded to match,
         1px less than the frame since it sits inside the border. */
      ${row_header === "header"
        ? css`
            border-top-left-radius: calc(${TABLE_BORDER_RADIUS}px - 1px);
          `
        : isLastRow &&
          css`
            border-bottom-left-radius: calc(${TABLE_BORDER_RADIUS}px - 1px);
          `}
    `;
//header

export const StyledDataTableHeader = styled.div`
  background-color: ${colors.white};
  border: 1px solid ${colors2.grey10};
  border-bottom: 1px solid ${colors2.grey10};
  border-top-left-radius: ${TABLE_BORDER_RADIUS}px;
  border-top-right-radius: ${TABLE_BORDER_RADIUS}px;
  display: flex;
  align-items: center;

  /* tableConfig.frameOutsideScroll: StyledTableFrame draws the outer edge,
     so only the line under the header stays. */
  ${({ $frameOutsideScroll }) =>
    $frameOutsideScroll &&
    css`
      border-width: 0 0 1px 0;
      border-radius: 0;
    `}
`;

/* tableConfig.frameOutsideScroll: the table's outer border on the scroll
   viewport instead of on the header + rows, so it stays put while the
   columns scroll sideways (with no pinned column, the left edge otherwise
   scrolls away with the first column). Hugs the table, capped at the
   container. */
export const StyledTableFrame = styled.div`
  width: fit-content;
  max-width: 100%;
  border: 1px solid ${colors2.grey10};
  border-radius: ${TABLE_BORDER_RADIUS}px;
  overflow: hidden;

  /* StickyHorizontalScrollbar's 1px bottom sentinel (desktop) sits inside
     the frame — tuck it under the last row so it leaves no gap above the
     bottom border. */
  & > [data-sticky-scroll-sentinel] {
    margin-top: -1px;
  }
`;

export const StyledDataTableHeaderCell = styled.div`
  flex: 0 0 auto;
  border-right: 1px solid ${colors2.grey10};
  // cursor: pointer;
  position: relative;


  ${({ hasError }) =>
    hasError &&
    css`
      &:not(:focus-within) {
        background-color: ${colors2.redBG};
      }
    `}

  ${({ headerEditable, cellIdx, isLastCell }) =>
    headerEditable &&
    css`
      &::after {
        content: "";
        position: absolute;
        z-index: -1;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        outline: 1px solid transparent;
        border-radius: ${cellIdx === 0
          ? "0.8rem 0 0 0"
          : isLastCell
          ? "0 0.8rem 0 0"
          : "0"};
        pointer-events: none;
      }

      &:hover::after {
        outline-color: ${colors2.grey30};
        z-index: 1;
      }

      &:focus-within::after {
        outline-color: ${colors2.grey80};
        box-shadow: 0 4px 10px #00000012;
        z-index: 2;
      }
    `}

  ${applyFreezeFirstColCss({ row_header: "header" })}
  /* The cell at the table's right edge draws no border of its own: the
     header's border is the edge, and it is the one that curves at the corner.
     A straight cell border there ran through the corner's curve. Last, so
     it wins over the frozen column's 4px when that column is also the edge —
     the same order the body cell uses. */
  ${({ isTableEdge }) =>
    isTableEdge &&
    css`
      border-right: none;
    `}
`;

export const StyledDataTableHeaderCellText = styled(UI_BODY_SEMIBOLD_SM_DF)`
  ${ellipsis}
  position: relative;
`;

// The compact header for dense tables (`tableConfig.compactHeader`): the
// same text on the caption face.
export const StyledDataTableHeaderCellTextCompact = styled(UI_CAPTION_SEMIBOLD_DF)`
  ${ellipsis}
  position: relative;
`;

export const StyledHeaderCellContentWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
  height: 100%;
  padding-top: 0.9rem;
  padding-bottom: 0.9rem;
  margin-right: 0.6rem;

  ${({ headerEditable }) =>
    headerEditable &&
    css`
      padding: 0;
      margin: 0;
      margin-right: 0.6rem;
    `}
`;

export const StyledHeaderErrorBanner = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${colors2.redText};
  color: ${colors2.white};
  padding: 0.4rem 0.8rem;
  border-bottom-left-radius: 0.6rem;
  border-bottom-right-radius: 0.6rem;
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  z-index: 10;

  svg {
    flex-shrink: 0;
  }
`;

//rows

export const StyledDataTableRowsContainer = styled.div`
  border: 1px solid ${colors2.grey10};
  border-bottom-left-radius: ${TABLE_BORDER_RADIUS}px;
  border-bottom-right-radius: ${TABLE_BORDER_RADIUS}px;
  border-top: none;

  ${({ $frameOutsideScroll }) =>
    $frameOutsideScroll &&
    css`
      border: none;
      border-radius: 0;
    `}
`;

export const StyledRowCellContentWrapper = styled.div`
  padding-left: 1rem;
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  position: relative;

  ${({ rightBleed, bleedColor }) =>
    rightBleed &&
    css`
      &::after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        width: 1rem;
        height: 100%;
        background: linear-gradient(
          to left,
          color-mix(in srgb, ${colors2.grey5}, ${colors2.white}),
          hsla(0, 0%, 100%, 0)
        );
        pointer-events: none; /* Allows clicks to pass through */
      }

    `}
`;

export const StyledTableRowCell = styled.div`
  flex: 0 0 auto;
  height: 100%;
  border-right: 1px solid ${colors2.grey10};
  ${applyFreezeFirstColCss({ row_header: "row" })}
  /* same as the header: the rows container's border is the right edge */
  ${({ isTableEdge }) =>
    isTableEdge &&
    css`
      border-right: none;
    `}
  ${({ highlight }) => {
    const colorLookup = {
      error: colors2.error,
    };
    if (highlight) {
      return css`
        border: 2px solid ${colorLookup[highlight]};
      `;
    }
  }}
   p, h1, h2, h3, h4, h5, h6 {
    ${ellipsis}
  }
`;

export const StyledCheckboxContainer = styled.div`
  /* css media query instead of the useScreenWidth hook, so that every
     row doesn't have to carry its own window resize listener */
  width: 42px;
  /* the data cells are flex: 0 0 auto (non-shrinking), so without this the
     checkbox column becomes the only shrinkable item and gets pinched below
     its width when the columns overflow, misaligning header vs body */
  flex-shrink: 0;
  min-height: 42px;
  max-height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-right: 1px solid ${colors2.grey10};

  @media only screen and (min-width: ${getBreakPoint("md")}px) {
    width: 36px;
    min-height: 36px;
  }
`;

export const StyledInput = styled.input`
  width: 100%;
  height: 100%;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  font-style: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  color: inherit;
  background: transparent;
  border: none;
  outline: none;
`;

export const StyledResizeDetector = styled.div`
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 2;
`;

export const StyledResizeFeedback = styled.div`
  position: absolute;
  top: 0;
  right: 50%;
  transform: translateX(50%);
  height: 100%;
  background-color: transparent;
  width: 0;
  transition: width 0.1s ease, background-color 0.1s ease;
  pointer-events: none;

  ${({ isHovered, isResizing, theme }) => {
    if (isResizing) {
      return css`
        width: 2px;
        background-color: ${theme.colors.primaryColor100};
      `;
    } else if (isHovered) {
      return css`
        width: 1px;
        background-color: ${theme.colors.primaryColor100};
      `;
    }
  }}
`;
